import CircularLoader from 'components/CircularLoader';
import { PanelRightIcon, SquareArrowDownLeftIcon, SquareArrowDownRightIcon } from 'components/icons';
import { animate, frame as motionFrame, m, useDragControls, useMotionValue, type PanInfo } from 'motion/react';
import { Ref, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { flushSync } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { AiSurface } from 'components/ui/overlay-motion';
import { useReducedMotion } from 'hooks/useReducedMotion';
import { useElevation } from 'hooks/useUiTheme';
import { useContentAreaRect } from 'layout/MainLayout/useContentAreaRect';
import { useDispatch, useSelector } from 'store';
import { clearFloatingPrompt, clearFloatingResume } from 'store/reducers/aiChat';
import { isTourMode } from 'tour/tourMode';
import { getBreadcrumbLeafKey } from 'utils/breadcrumbLeaf';
import { cn } from 'utils/cn';
import { getLocalStorage, setLocalStorage } from 'utils/localStorage';
import { SPRING } from 'utils/motion';
import { AiChatOptionPickHandlers, composerActions } from '../aiChatOptionPickContext';
import AiChatConversation from '../components/AiChatConversation';
import AiChatResumeBanner from '../components/AiChatResumeBanner';
import AiChatThreadList from '../components/AiChatThreadList';
import ChatComposer, { ChatComposerHandle } from '../components/ChatComposer';
import { AiChatSurfaceController } from '../AiChatSessionProvider';
import type { AiChatRequestRecord } from '../hooks/useAiChatController';
import { PromptSelectHandler } from '../hooks/usePlaceholderFill';
import FloatingAiEmptyState from './FloatingAiEmptyState';
import FloatingAiPanelHeader from './FloatingAiPanelHeader';
import { readSeededRecordPrompt, SeededPromptPreview } from './SeededPromptPreview';
import { getPromptKeys } from './pagePrompts';
import {
    canFitHistoryColumn,
    clamp,
    dockedIn,
    HISTORY_WIDTH,
    landingDock,
    liftedBox,
    PanelDock,
    PanelExtent,
    PanelPlacement,
    parsePlacement,
    resolvePanelFrame,
    stepDock,
    toggledDock,
} from './panelGeometry';
import { PageContext } from './usePageContext';

const HISTORY_OPEN_STORAGE_KEY = 'ai-chat-floating-history-open';
const EXPANDED_STORAGE_KEY = 'ai-chat-floating-expanded';
const DOCK_STORAGE_KEY = 'ai-chat-floating-dock';

const AT_ONCE = { duration: 0 } as const;
/** How far the pointer moves before a press on the header lifts the panel out of the side dock. */
const LIFT_DISTANCE = 4;
/** A focused edge moves this far per arrow key, four times as far with Shift. */
const EDGE_STEP = 16;
/** Corners are drawn rounded-2xl; set in `style` as px, which Motion's scale correction reads. The shadow is beside them. */
const PANEL_RADIUS = 16;
const DOCKED_MESSAGE: Record<PanelDock, string> = {
    left: 'ai-chat.floating.docked-left',
    right: 'ai-chat.floating.docked-right',
    side: 'ai-chat.floating.docked-side',
};

const subscribeToResize = (onResize: () => void) => {
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
};
const viewportWidth = () => window.innerWidth;
const viewportHeight = () => window.innerHeight;

/** Where a press is on one of the header's own controls, which never starts a drag. */
const isOnControl = (target: EventTarget) =>
    target instanceof Element && target.closest('button, a, input, textarea, [role="button"]') !== null;

/** Where Alt+Shift+arrow keeps selecting by word, so the dock shortcut leaves it alone. */
const isTextField = (target: EventTarget) =>
    target instanceof Element &&
    target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])') !== null;

interface FloatingAiPanelProps {
    /** The panel's card: what a prompt scoped to the panel covers. */
    ref?: Ref<HTMLDivElement>;
    controller: AiChatSurfaceController;
    pageContext: PageContext | null;
    /**
     * True while the panel is showing the conversation the full AI page handed over. The handoff
     * exists to keep the page the user navigated to visible under the conversation, so it always
     * lands windowed — expanded mode would cover that page entirely. Read when the panel mounts.
     */
    isHandoff: boolean;
    /** Whether a row sits along the window's bottom edge, for the panel docked bottom right to stop above. */
    reserveBottomRow?: boolean;
    /** Whether a dialog or drawer the panel stays under is open: it then takes the side dock's layer, in any dock. */
    isUnderDialog?: boolean;
    /**
     * Whether the page wants a corner panel in the side dock, beside it rather than over it, until the user moves
     * the panel. The remembered placement stays as it is.
     */
    prefersSideDock?: boolean;
    /** Plays the exit transition; the owner unmounts the panel once it finishes. */
    isClosing: boolean;
    onClose: () => void;
    // Switching conversations belongs to the owner: the panel renders whichever session it is
    // handed, which is not always the one these act on.
    onSelectThread: (threadId: number) => void;
    onNewChat: () => void;
    onDeleteThread: (threadId: number) => void;
}

const FloatingAiPanel: React.FC<FloatingAiPanelProps> = ({
    ref,
    controller,
    pageContext,
    isHandoff,
    reserveBottomRow = true,
    isUnderDialog = false,
    prefersSideDock = false,
    isClosing,
    onClose,
    onSelectThread,
    onNewChat,
    onDeleteThread,
}) => {
    const intl = useIntl();
    const {
        threadId,
        isHistoryFetching,
        isStreaming,
        userVisibleMessages,
        setStartingPromptId,
        send,
        stopGeneration,
        retryLastMessage,
    } = controller;

    // An opener outside the panel (an "ask AI about this" affordance) can hand the panel a question
    // to start from: it opens with that already in the composer, and the reader still sends it.
    const seededPrompt = useSelector((state) => state.aiChat.floatingPrompt);
    const [input, setInput] = useState(seededPrompt ?? '');
    // Which question the composer has already been given, so a re-render does not overwrite the
    // reader's edits with it. The seed itself is consumed below rather than left standing: an
    // affordance clicked twice hands over the same string, and comparing values alone would read
    // the second click as nothing having changed.
    const [seedInComposer, setSeedInComposer] = useState(seededPrompt);
    /*
     * The seeded question for as long as it is still exactly what was handed over, and null the
     * moment the reader takes it on. It cannot be read off the seed above: that one is consumed
     * out of the store as soon as it lands, so by the next render there is nothing left to compare
     * the composer against. A question naming a record is shown as it will read once sent while
     * this holds it, and each new seed starts that over.
     */
    const [untouchedSeed, setUntouchedSeed] = useState(seededPrompt);
    if (seedInComposer !== seededPrompt) {
        setSeedInComposer(seededPrompt);
        if (seededPrompt !== null) {
            setInput(seededPrompt);
            setUntouchedSeed(seededPrompt);
        }
    }
    // A handoff overrides the remembered expanded mode for this opening only — the stored
    // preference is left alone, so a later manual open still restores it. Mount-time is enough:
    // a handoff reaching an already-open panel can only come from a link on the page underneath,
    // which an expanded panel would have covered.
    const [isExpanded, setIsExpanded] = useState(() => !isHandoff && getLocalStorage(EXPANDED_STORAGE_KEY) === 'true');
    // Expanded mode covers the whole content area, so navigating the app underneath it — a menu
    // click, a link on the page — gives the new page back to the reader. Only this opening: the
    // remembered preference is left alone, as it is on a handoff.
    const { pathname } = useLocation();
    const [expandedOnPathname, setExpandedOnPathname] = useState(pathname);
    if (expandedOnPathname !== pathname) {
        setExpandedOnPathname(pathname);
        setIsExpanded(false);
    }
    // Closing the panel unmounts it, so the history column would forget it was open. Remembered
    // across panel openings (and reloads) rather than reset every time.
    const [isHistoryOpen, setIsHistoryOpen] = useState(() => getLocalStorage(HISTORY_OPEN_STORAGE_KEY) === 'true');
    // The dock and the user's size, remembered across pages, reloads and new chats.
    const [placement, setPlacement] = useState(() => parsePlacement(getLocalStorage(DOCK_STORAGE_KEY)));
    // True from a change of dock or of expanded mode until Motion's glide into it ends; every other change of the
    // panel's box (a resize, a window resize, the content area moving) is drawn at once.
    const [isGliding, setIsGliding] = useState(false);
    // Whether the user moved the panel in this opening, which ends `prefersSideDock`'s hold on it.
    const [hasMoved, setHasMoved] = useState(false);
    // True from opening or closing the history column in a corner until the width's ease to it ends: the one change
    // of the width that eases.
    const [isEasingWidth, setIsEasingWidth] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [dragTarget, setDragTarget] = useState<PanelDock | null>(null);
    // The panel pulled out of the side dock, at its corner size under the pointer, for the drag that follows.
    const [lifted, setLifted] = useState<ReturnType<typeof liftedBox> | null>(null);
    const [isResizing, setIsResizing] = useState(false);
    // Counts the drops: each one sends the drag's offset home, after the commit that moves the dock.
    const [drops, setDrops] = useState(0);
    // Mounted closed, then opened, so the panel reveal has a state to move from.
    const [isRevealed, setIsRevealed] = useState(false);
    const composerRef = useRef<ChatComposerHandle>(null);
    const dispatch = useDispatch();
    const breadcrumbLeaf = useSelector((state) => state.breadcrumb);
    const floatingResume = useSelector((state) => state.aiChat.floatingResume);
    const contentRect = useContentAreaRect();
    const viewport = {
        width: useSyncExternalStore(subscribeToResize, viewportWidth),
        height: useSyncExternalStore(subscribeToResize, viewportHeight),
    };
    const still = useReducedMotion();
    // Read off the panel's own surface, which draws in the new design in classic too.
    const [surface, setSurface] = useState<HTMLDivElement | null>(null);
    const panelShadow = useElevation('panel', surface);
    const dragShadow = useElevation('drag', surface);
    const dragControls = useDragControls();
    const dragX = useMotionValue(0);
    const dragY = useMotionValue(0);
    // Whether a drag out of the side dock has stayed in the dock's strip so far: let go there, it stays docked.
    const inSideStripRef = useRef(false);
    // Whether Motion's drag took over the press that lifted the panel; one let go before it did puts the panel back.
    const dragStartedRef = useRef(false);

    // Only for as long as the resumed conversation is the one on screen: deleting it (or anything
    // else clearing the thread) takes the "continuing your chat" message with it.
    const resumedAt =
        floatingResume != null && floatingResume.threadId === threadId ? floatingResume.lastActiveAt : null;

    const isEmptyState = !isHistoryFetching && !threadId && userVisibleMessages.length === 0 && !isStreaming;
    const promptKeys = getPromptKeys(pageContext?.objectType);
    const title = intl.formatMessage({ id: 'ai-chat.title' });

    const focusComposer = useCallback(() => composerRef.current?.focus(), []);
    const handlePromptSelect: PromptSelectHandler = (...prompt) => composerRef.current?.selectPrompt(...prompt);
    const seededRecord = input === untouchedSeed ? readSeededRecordPrompt(input) : null;
    // The field is unmounted until the preview goes, so without the sync commit the ref is null.
    const editSeededPrompt = () => {
        flushSync(() => setUntouchedSeed(null));
        focusComposer();
    };

    // The record's display name is published globally by the page it belongs to (breadcrumb slice,
    // keyed by `getBreadcrumbLeafKey`). Reuse it when it matches the current page; the panel itself never
    // loads page data. Null on lists, unknown pages, or while the record is still loading.
    const recordName = useMemo(() => {
        if (!pageContext || pageContext.id == null) {
            return null;
        }
        const leafKey = getBreadcrumbLeafKey(pageContext.url, { id: pageContext.id });
        return breadcrumbLeaf.leafKey === leafKey ? breadcrumbLeaf.leafLabel : null;
    }, [pageContext, breadcrumbLeaf]);

    // A seeded question is taken out of the store as soon as it is in the composer, so the store
    // holds one only in transit. That is what makes every click of an "ask AI about this"
    // affordance land: the next one is a change from nothing again, whether the reader edited the
    // last one, sent it, or left it alone. The caret goes after it, where the reader carries on.
    useEffect(() => {
        if (seededPrompt === null) {
            return;
        }
        dispatch(clearFloatingPrompt());
        focusComposer();
    }, [dispatch, focusComposer, seededPrompt]);

    useEffect(() => {
        const frame = requestAnimationFrame(() => setIsRevealed(true));
        return () => cancelAnimationFrame(frame);
    }, []);

    const handleSend = (text: string, promptId: string | null, records: AiChatRequestRecord[]) => {
        // Sending is the user picking the conversation up, so the resume banner has said its piece.
        dispatch(clearFloatingResume());
        setStartingPromptId(promptId);
        void send(text, { records });
    };

    // Clicking a choice the assistant offered is an ordinary send, minus the composer's own housekeeping:
    // the draft is left alone, and so is its placeholder tracking, which is what still refuses to send a
    // draft holding a sample prompt's bare "[…]".
    const optionPick: AiChatOptionPickHandlers = {
        onSelect: (text) => {
            if (isStreaming) {
                return;
            }
            dispatch(clearFloatingResume());
            void send(text);
        },
        isDisabled: isStreaming,
        retry: () => void retryLastMessage(),
        ...composerActions(composerRef),
    };

    // Following an entity link navigates the app underneath the panel, which the route change above
    // already collapses for — but a link to the page already open changes no route, and expanded mode
    // would go on covering it.
    const handleInternalLinkNavigate = useCallback(() => setIsExpanded(false), []);

    // Opening the panel is a request to type in it; the same on starting a new chat.
    useEffect(() => {
        focusComposer();
    }, [focusComposer]);

    const handleNewChat = () => {
        onNewChat();
        focusComposer();
    };

    const toggleExpanded = () => {
        const nextIsExpanded = !isExpanded;
        setIsGliding(true);
        setIsExpanded(nextIsExpanded);
        if (!isTourMode()) setLocalStorage(EXPANDED_STORAGE_KEY, String(nextIsExpanded));
    };

    const shownPlacement = prefersSideDock && !hasMoved ? dockedIn(placement, 'side') : placement;
    const frameInput = { placement: shownPlacement, isExpanded, reserveBottomRow, contentRect, viewport };
    // On a panel too narrow for both columns the history is not offered, rather than offered and
    // leaving nothing to read the conversation in.
    const canShowHistory = canFitHistoryColumn(frameInput);
    const isHistoryVisible = isHistoryOpen && canShowHistory;
    const frame = resolvePanelFrame({ ...frameInput, isHistoryOpen: isHistoryVisible });
    const frameIn = (side: PanelDock) =>
        resolvePanelFrame({ ...frameInput, placement: dockedIn(placement, side), isHistoryOpen: isHistoryVisible });
    const { dock, canMove, canSideDock } = frame;
    const isMovable = canMove && !isExpanded;
    const box = lifted ?? frame.box;
    // Part of the page there: square, a rule on its inner edge, no shadow, and under every page dialog.
    const isInSideDock = dock === 'side' && !isExpanded && lifted === null;
    // The page narrows beside the side dock, by the history column too, and widens again as the panel leaves it.
    const sideDockWidth = dock === 'side' && !isExpanded && !isClosing ? frame.box.width : 0;
    // Under reduced motion the layouts' `MotionConfig` makes the glide a jump.
    const layoutTransition = { layout: isGliding ? SPRING : AT_ONCE };

    // In the side dock the page narrows at once, so the panel beside it does too.
    const toggleHistory = () => {
        const nextIsHistoryOpen = !isHistoryOpen;
        setIsEasingWidth(canShowHistory && !isExpanded && !isInSideDock && !still);
        setIsHistoryOpen(nextIsHistoryOpen);
        if (!isTourMode()) setLocalStorage(HISTORY_OPEN_STORAGE_KEY, String(nextIsHistoryOpen));
    };

    useLayoutEffect(() => {
        if (!sideDockWidth) {
            return;
        }
        const { style } = document.documentElement;
        style.setProperty('--ai-side-dock-width', `${sideDockWidth}px`);
        return () => {
            style.removeProperty('--ai-side-dock-width');
        };
    }, [sideDockWidth]);

    // A dropped panel glides on from where it was let go: the dock's layout glide moves the box, and the drag's offset
    // goes home to 0 on the same clock, started by the commit that moved the box so the two stay in step.
    useLayoutEffect(() => {
        if (drops === 0) {
            return;
        }
        const home = still ? AT_ONCE : SPRING;
        void animate(dragX, 0, home);
        void animate(dragY, 0, home);
    }, [drops, dragX, dragY, still]);

    const isHandlingPanel = isDragging || isResizing;
    useEffect(() => {
        if (!isHandlingPanel) {
            return;
        }
        document.body.classList.add('select-none');
        return () => document.body.classList.remove('select-none');
    }, [isHandlingPanel]);

    const remember = (next: PanelPlacement) => {
        if (!isTourMode()) setLocalStorage(DOCK_STORAGE_KEY, JSON.stringify(next));
    };

    const dockIn = (next: PanelDock) => {
        const nextPlacement = dockedIn(placement, next);
        setHasMoved(true);
        setPlacement(nextPlacement);
        remember(nextPlacement);
        composerRef.current?.announce(intl.formatMessage({ id: DOCKED_MESSAGE[next] }));
    };

    const moveTo = (next: PanelDock) => {
        if (next === dock) {
            return;
        }
        setIsGliding(true);
        dockIn(next);
    };

    const landing = (info: PanInfo) => {
        if (info.point.x < viewport.width - frame.box.width) {
            inSideStripRef.current = false;
        }
        return landingDock({
            pointerX: info.point.x,
            panelCentreX: box.left + dragX.get() + box.width / 2,
            inSideStrip: inSideStripRef.current,
            canSideDock,
            contentRect,
            viewport,
        });
    };

    const handleDragStart = () => {
        dragStartedRef.current = true;
        inSideStripRef.current = dock === 'side';
        setIsGliding(false);
        setIsDragging(true);
    };

    const handleDragEnd = (_event: unknown, info: PanInfo) => {
        const target = landing(info);
        setIsDragging(false);
        setDragTarget(null);
        setLifted(null);
        setDrops((count) => count + 1);
        if (target !== dock || lifted !== null) {
            setIsGliding(true);
        }
        if (target !== dock) {
            dockIn(target);
        }
    };

    // Out of the side dock the panel first takes its corner size under the pointer, then Motion's drag takes over:
    // laid out there before the drag starts, so the drag's constraints and its offset start from that box.
    const liftOutOfSideDock = (press: React.PointerEvent) => {
        const from = { x: press.clientX, y: press.clientY };
        const grabY = press.clientY - frame.box.top;
        const { width, height } = frameIn(placement.corner).box;
        const stop = () => {
            window.removeEventListener('pointermove', follow);
            window.removeEventListener('pointerup', stop);
            window.removeEventListener('pointercancel', stop);
        };
        const follow = (event: PointerEvent) => {
            if (Math.hypot(event.clientX - from.x, event.clientY - from.y) < LIFT_DISTANCE) {
                return;
            }
            stop();
            flushSync(() =>
                setLifted(liftedBox({ width, height }, { x: event.clientX, y: event.clientY }, grabY, viewport))
            );
            dragStartedRef.current = false;
            dragControls.start(event);
            // A release can still start Motion's drag, from a move it had not drawn yet; past Motion's next frame the
            // drag has either begun, and ends through `handleDragEnd`, or never will.
            const settle = () => {
                window.removeEventListener('pointerup', settle);
                window.removeEventListener('pointercancel', settle);
                motionFrame.postRender(() => {
                    if (!dragStartedRef.current) {
                        setIsGliding(true);
                        setLifted(null);
                    }
                });
            };
            window.addEventListener('pointerup', settle);
            window.addEventListener('pointercancel', settle);
        };
        window.addEventListener('pointermove', follow);
        window.addEventListener('pointerup', stop);
        window.addEventListener('pointercancel', stop);
    };

    // The header is the handle, anywhere but its buttons and links.
    const handleHandlePointerDown = (event: React.PointerEvent) => {
        if (!isMovable || event.button !== 0 || isOnControl(event.target)) {
            return;
        }
        if (dock === 'side') {
            liftOutOfSideDock(event);
        } else {
            dragControls.start(event);
        }
    };

    const handleHandleDoubleClick = (event: React.MouseEvent) => {
        if (isMovable && !isOnControl(event.target)) {
            moveTo(toggledDock(dock, placement.corner));
        }
    };

    // Escape closes the panel, unless a placeholder fill session claimed the key first — its own
    // handler marks that by preventing the default — or a record's peek card is up: this runs before
    // the card's own Escape, which closes the card and leaves the panel open. Alt+Shift+← / → moves it
    // through the docks, outside its text fields.
    const handlePanelKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Escape') {
            if (!event.defaultPrevented && !document.querySelector('[data-peek-card]')) {
                onClose();
            }
            return;
        }
        if (
            isMovable &&
            event.altKey &&
            event.shiftKey &&
            (event.key === 'ArrowLeft' || event.key === 'ArrowRight') &&
            !isTextField(event.target)
        ) {
            event.preventDefault();
            moveTo(stepDock(dock, event.key === 'ArrowLeft' ? -1 : 1, canSideDock));
        }
    };

    // An edge follows the pointer from where it was pressed, the dock's outer bottom corner staying put; remembered
    // once let go.
    const startResize = (edges: 'width' | 'height' | 'both') => (event: React.PointerEvent<HTMLElement>) => {
        const { width, height } = frame;
        if (event.button !== 0 || !width) {
            return;
        }
        event.preventDefault();
        const grip = event.currentTarget;
        grip.setPointerCapture(event.pointerId);
        const from = { x: event.clientX, y: event.clientY };
        // The inner edge is the left one but bottom left, where the panel grows to the right.
        const outwards = dock === 'left' ? 1 : -1;
        let next = placement;
        setIsGliding(false);
        setIsResizing(true);
        const follow = (move: PointerEvent) => {
            next = {
                ...placement,
                width:
                    edges === 'height'
                        ? placement.width
                        : clamp(width.now + (move.clientX - from.x) * outwards, width.min, width.max),
                height:
                    edges === 'width' || !height
                        ? placement.height
                        : clamp(height.now + from.y - move.clientY, height.min, height.max),
            };
            setPlacement(next);
        };
        const end = () => {
            grip.removeEventListener('pointermove', follow);
            grip.removeEventListener('lostpointercapture', end);
            setIsResizing(false);
            remember(next);
        };
        grip.addEventListener('pointermove', follow);
        grip.addEventListener('lostpointercapture', end);
    };

    // A focused edge moves 16px per arrow key (64px with Shift), outwards or in; Home and End go to the smallest and
    // the largest size the dock allows. Alt is the dock shortcut's, which the panel handles.
    const nudgeEdge = (axis: 'width' | 'height', size: PanelExtent) => (event: React.KeyboardEvent) => {
        if (event.altKey) {
            return;
        }
        const outwards = axis === 'height' ? 'ArrowUp' : dock === 'left' ? 'ArrowRight' : 'ArrowLeft';
        const inwards = axis === 'height' ? 'ArrowDown' : dock === 'left' ? 'ArrowLeft' : 'ArrowRight';
        const step = event.shiftKey ? 4 * EDGE_STEP : EDGE_STEP;
        const value =
            event.key === 'Home'
                ? size.min
                : event.key === 'End'
                  ? size.max
                  : event.key === outwards
                    ? size.now + step
                    : event.key === inwards
                      ? size.now - step
                      : null;
        if (value === null) {
            return;
        }
        event.preventDefault();
        const next = { ...placement, [axis]: clamp(value, size.min, size.max) };
        setPlacement(next);
        remember(next);
    };

    const showsGrips = isMovable && !isDragging && lifted === null;

    return (
        <AiSurface
            ref={setSurface}
            role="dialog"
            aria-label={title}
            aria-keyshortcuts={isMovable ? 'Alt+Shift+ArrowLeft Alt+Shift+ArrowRight' : undefined}
            // Record chips in the panel get the app's peek card, which a dialog otherwise opts out of.
            data-peek="on"
            onKeyDown={handlePanelKeyDown}
            onTransitionEnd={(event) => {
                if (event.target === event.currentTarget && event.propertyName === 'width') setIsEasingWidth(false);
            }}
            style={lifted ?? frame.style}
            // The box only jumps; Motion draws the card's way there. Its width follows the history column on card
            // resize's clock in a corner, but not while a glide, a drag or a resize draws the card.
            className={cn(
                'text-foreground pointer-events-none fixed flex',
                // On its way into the side dock it still passes over the page's chrome.
                (isInSideDock && !isGliding) || isUnderDialog
                    ? 'z-(--z-floating-ai-side-dock)'
                    : 'z-(--z-floating-ai-panel)',
                isEasingWidth &&
                    !isGliding &&
                    !isHandlingPanel &&
                    'transition-[width] duration-(--resize-dur) ease-(--resize-ease) motion-reduce:transition-none'
            )}
        >
            {isMovable ? (
                <DockTargets
                    target={dragTarget}
                    boxes={{
                        left: frameIn('left').box,
                        right: frameIn('right').box,
                        side: canSideDock ? frameIn('side').box : null,
                    }}
                />
            ) : null}
            <div data-open={isRevealed && !isClosing} className="t-panel-slide relative flex min-w-0 flex-1">
                <m.div
                    ref={ref}
                    layout
                    drag
                    dragControls={dragControls}
                    dragListener={false}
                    dragMomentum={false}
                    dragElastic={0.05}
                    // The window, from where the box is laid out.
                    dragConstraints={{
                        left: -box.left,
                        top: -box.top,
                        right: viewport.width - box.left - box.width,
                        bottom: viewport.height - box.top - box.height,
                    }}
                    onDragStart={handleDragStart}
                    onDrag={(_event, info) => setDragTarget(landing(info))}
                    onDragEnd={handleDragEnd}
                    onLayoutAnimationComplete={() => setIsGliding(false)}
                    transition={layoutTransition}
                    style={{
                        x: dragX,
                        y: dragY,
                        borderRadius: isExpanded || isInSideDock ? 0 : PANEL_RADIUS,
                        boxShadow: isDragging ? dragShadow : isInSideDock ? 'none' : panelShadow,
                    }}
                    className={cn(
                        'border-border bg-card relative flex min-w-0 flex-1 overflow-hidden transition-shadow duration-(--duration-quick) ease-(--ease-smooth-out)',
                        // Floating, the elevation's ring is its edge; docked to the side, a rule on its inner edge.
                        isInSideDock ? 'border-s' : 'outline-hidden'
                    )}
                >
                    {/* The panel grows by this column, so the conversation keeps its width; the list keeps its
                        own width inside and clips. Closed, `invisible` (after the resize) takes it out of the
                        tab order and the accessibility tree. */}
                    <m.div
                        layout="position"
                        transition={layoutTransition}
                        className={cn(
                            'shrink-0 overflow-hidden transition-[width,opacity,visibility] duration-(--resize-dur) ease-(--resize-ease) motion-reduce:transition-[opacity,visibility] motion-reduce:duration-(--duration-quick)',
                            !isHistoryVisible && 'invisible opacity-0'
                        )}
                        style={{ width: isHistoryVisible ? HISTORY_WIDTH : 0 }}
                    >
                        <div className="border-border flex h-full flex-col border-e" style={{ width: HISTORY_WIDTH }}>
                            <h2 className="text-section text-foreground m-0 flex h-10 shrink-0 items-center px-4 pt-2">
                                <FormattedMessage id="ai-chat.floating.history" />
                            </h2>
                            <div className="min-h-0 flex-1">
                                <AiChatThreadList
                                    selectedThreadId={threadId}
                                    onSelectThread={onSelectThread}
                                    onDeleteThread={onDeleteThread}
                                />
                            </div>
                        </div>
                    </m.div>
                    <div className="flex min-w-0 flex-1 flex-col">
                        {/* The handle. The header, the conversation and the composer keep their own size through a
                            glide, so their text and icons do not stretch while the card's box scales. */}
                        <m.div
                            layout="position"
                            transition={layoutTransition}
                            onPointerDown={handleHandlePointerDown}
                            onDoubleClick={handleHandleDoubleClick}
                            // The press that starts a move selects no text, and what the pointer runs onto in the
                            // header while the panel is held at the window's edge opens no tooltip or peek.
                            className={cn(
                                'shrink-0',
                                isMovable && 'cursor-move touch-none select-none',
                                isDragging && '[&_a]:pointer-events-none'
                            )}
                        >
                            <FloatingAiPanelHeader
                                title={title}
                                pageContext={pageContext}
                                recordName={recordName}
                                isHistoryOpen={isHistoryVisible}
                                canToggleHistory={canShowHistory}
                                isExpanded={isExpanded}
                                onToggleHistory={toggleHistory}
                                onNewChat={handleNewChat}
                                onToggleExpanded={toggleExpanded}
                                onClose={onClose}
                                isMoving={isDragging}
                            />
                        </m.div>

                        <m.div layout="position" transition={layoutTransition} className="flex min-h-0 flex-1 flex-col">
                            {resumedAt != null ? (
                                <AiChatResumeBanner
                                    surface="floating"
                                    resumedAt={resumedAt}
                                    onNewChat={handleNewChat}
                                />
                            ) : null}

                            {isHistoryFetching ? (
                                <div className="flex min-h-0 flex-1 items-center justify-center">
                                    <CircularLoader className="static [&>span]:size-6" />
                                </div>
                            ) : isEmptyState ? (
                                <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-4 pt-4 pb-6">
                                    <FloatingAiEmptyState
                                        promptKeys={promptKeys}
                                        onPromptSelect={handlePromptSelect}
                                        onSelectThread={onSelectThread}
                                    />
                                </div>
                            ) : (
                                <AiChatConversation
                                    controller={controller}
                                    optionPick={optionPick}
                                    onInternalLinkNavigate={handleInternalLinkNavigate}
                                    // A table keeps each cell on one line here and scrolls sideways in its own box.
                                    contentClassName="[&_table]:whitespace-nowrap"
                                />
                            )}
                        </m.div>

                        <m.div layout="position" transition={layoutTransition} className="shrink-0">
                            <ChatComposer
                                ref={composerRef}
                                variant="panel"
                                value={input}
                                onValueChange={setInput}
                                placeholder={intl.formatMessage({ id: 'ai-chat.floating.input-placeholder' })}
                                isStreaming={isStreaming}
                                onSend={handleSend}
                                onStop={stopGeneration}
                                preview={
                                    seededRecord ? (
                                        <SeededPromptPreview prompt={seededRecord} onEdit={editSeededPrompt} />
                                    ) : undefined
                                }
                            />
                        </m.div>
                    </div>
                </m.div>
                {showsGrips && frame.width ? (
                    <ResizeGrips
                        dock={dock}
                        width={frame.width}
                        height={frame.height}
                        onResize={startResize}
                        onNudge={nudgeEdge}
                    />
                ) : null}
            </div>
        </AiSurface>
    );
};

type Box = ReturnType<typeof liftedBox>;

const DOCK_TARGET: Record<PanelDock, { label: string; Icon: typeof PanelRightIcon; origin: string }> = {
    left: { label: 'ai-chat.floating.dock-left', Icon: SquareArrowDownLeftIcon, origin: 'origin-bottom-left' },
    right: { label: 'ai-chat.floating.dock-right', Icon: SquareArrowDownRightIcon, origin: 'origin-bottom-right' },
    side: { label: 'ai-chat.floating.dock-side', Icon: PanelRightIcon, origin: 'origin-right' },
};

/**
 * While the panel is dragged, the dock it will land in, at that dock's box and the panel's size: it fades in from
 * a slight scale, grown from the dock's corner (the window's right edge for the side dock), and swaps as the panel
 * crosses over. The side dock's is a full-height strip 8px inside the window, its pill at the top, clear of the panel
 * dragged over it.
 */
function DockTargets({ target, boxes }: { target: PanelDock | null; boxes: Record<PanelDock, Box | null> }) {
    const intl = useIntl();
    return (Object.keys(DOCK_TARGET) as PanelDock[]).map((dock) => {
        const box = boxes[dock];
        if (!box) {
            return null;
        }
        const { label, Icon, origin } = DOCK_TARGET[dock];
        const isSide = dock === 'side';
        return (
            <div
                key={dock}
                aria-hidden
                className={cn(
                    'border-primary bg-primary-wash/70 pointer-events-none fixed grid rounded-2xl border-2 transition-[opacity,scale,visibility] ease-out',
                    origin,
                    isSide ? 'content-start justify-items-center pt-6' : 'place-items-center',
                    target === dock
                        ? 'duration-(--duration-quick)'
                        : 'invisible opacity-0 duration-50 motion-safe:scale-(--scale-small)'
                )}
                style={
                    isSide
                        ? { left: box.left + 8, top: 8, width: box.width - 16, height: box.height - 16 }
                        : { left: box.left, top: box.top, width: box.width, height: box.height }
                }
            >
                <span className="bg-card text-meta text-primary-ink shadow-menu inline-flex items-center gap-1.5 rounded-full py-1.5 ps-2.5 pe-3 font-medium">
                    <Icon className="size-4" />
                    {intl.formatMessage({ id: label })}
                </span>
            </div>
        );
    });
}

/**
 * The resize grips, invisible, on an overlay reaching 4px past the panel: in a corner the inner edge, the top edge
 * and the corner between them; in the side dock the inner edge, full height. The two edges are focusable
 * separators, which show a strip while focused; the corner is a pointer shortcut only.
 */
function ResizeGrips({
    dock,
    width,
    height,
    onResize,
    onNudge,
}: {
    dock: PanelDock;
    width: PanelExtent;
    height: PanelExtent | null;
    onResize: (edges: 'width' | 'height' | 'both') => (event: React.PointerEvent<HTMLElement>) => void;
    onNudge: (axis: 'width' | 'height', size: PanelExtent) => (event: React.KeyboardEvent) => void;
}) {
    const intl = useIntl();
    const edge = 'focus-visible:bg-primary absolute touch-none rounded-full outline-none';
    const innerSide = dock === 'left' ? '-right-1' : '-left-1';
    return (
        <>
            <span
                role="separator"
                tabIndex={0}
                aria-orientation="vertical"
                aria-label={intl.formatMessage({ id: 'ai-chat.floating.panel-width' })}
                aria-valuenow={width.now}
                aria-valuemin={width.min}
                aria-valuemax={width.max}
                onPointerDown={onResize('width')}
                onKeyDown={onNudge('width', width)}
                className={cn(edge, innerSide, 'w-2 cursor-ew-resize', dock === 'side' ? 'inset-y-0' : 'inset-y-3')}
            />
            {height ? (
                <>
                    <span
                        role="separator"
                        tabIndex={0}
                        aria-orientation="horizontal"
                        aria-label={intl.formatMessage({ id: 'ai-chat.floating.panel-height' })}
                        aria-valuenow={height.now}
                        aria-valuemin={height.min}
                        aria-valuemax={height.max}
                        onPointerDown={onResize('height')}
                        onKeyDown={onNudge('height', height)}
                        className={cn(edge, 'inset-x-3 -top-1 h-2 cursor-ns-resize')}
                    />
                    <span
                        aria-hidden
                        onPointerDown={onResize('both')}
                        className={cn(
                            'absolute -top-1 size-4 touch-none',
                            innerSide,
                            dock === 'left' ? 'cursor-nesw-resize' : 'cursor-nwse-resize'
                        )}
                    />
                </>
            ) : null}
        </>
    );
}

export default FloatingAiPanel;
